import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'

/**
 * Dashboard Uptime Bars Controller
 *
 * Horizontal bar chart for uptime history.
 * Fetches data via AJAX and subscribes to ActionCable for real-time updates.
 */
export default class extends Controller {
  static values = {
    websiteId: String,
    days: { type: Number, default: 14 }
  }

  connect() {
    this.heatmapData = {}
    this.tooltip = null
    this.createTooltip()
    this.fetchData()
    this.subscribeToChannel()
  }

  disconnect() {
    this.unsubscribeFromChannel()
    this.removeTooltip()
  }

  // ==================== Tooltip ====================

  createTooltip() {
    this.tooltip = document.createElement('div')
    this.tooltip.className = 'uptime-tooltip'
    this.tooltip.style.cssText = `
      position: fixed;
      z-index: 9999;
      padding: 8px 12px;
      background: var(--bg-elevated, #1f2937);
      color: var(--text-on-dark, #fff);
      border-radius: 6px;
      font-size: 12px;
      line-height: 1.4;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s ease;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      white-space: normal;
      min-width: 120px;
    `
    document.body.appendChild(this.tooltip)
  }

  removeTooltip() {
    if (this.tooltip && this.tooltip.parentNode) {
      this.tooltip.parentNode.removeChild(this.tooltip)
    }
  }

  showTooltip(event, content) {
    if (!this.tooltip) return

    this.tooltip.innerHTML = content
    this.tooltip.style.opacity = '1'

    // Position tooltip above the bar
    const rect = event.target.getBoundingClientRect()
    const tooltipRect = this.tooltip.getBoundingClientRect()

    let left = rect.left + (rect.width / 2) - (tooltipRect.width / 2)
    let top = rect.top - tooltipRect.height - 8

    // Keep tooltip within viewport
    if (left < 8) left = 8
    if (left + tooltipRect.width > window.innerWidth - 8) {
      left = window.innerWidth - tooltipRect.width - 8
    }
    if (top < 8) {
      top = rect.bottom + 8 // Show below if no room above
    }

    this.tooltip.style.left = `${left}px`
    this.tooltip.style.top = `${top}px`
  }

  hideTooltip() {
    if (this.tooltip) {
      this.tooltip.style.opacity = '0'
    }
  }

  // ==================== ActionCable ====================

  subscribeToChannel() {
    this.subscription = consumer.subscriptions.create(
      { channel: 'UptimeChannel', website_id: this.websiteIdValue },
      {
        connected: () => {
          console.log('[Dashboard:UptimeBars] ActionCable connected')
        },
        disconnected: () => {
          console.log('[Dashboard:UptimeBars] ActionCable disconnected')
        },
        received: (data) => this.handleBroadcast(data)
      }
    )
  }

  unsubscribeFromChannel() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  handleBroadcast(data) {
    if (data.event === 'status_update') {
      // Refetch heatmap data when we get a status update
      // This ensures the bars reflect the latest check
      this.fetchData()
    }
  }

  // ==================== Data Fetching ====================

  async fetchData() {
    try {
      const response = await fetch(`/ajax/uptime/${this.websiteIdValue}/heatmap?days=${this.daysValue}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        console.error('[Dashboard:UptimeBars] HTTP error:', response.status)
        return
      }

      this.heatmapData = await response.json()
      this.renderBars()
    } catch (e) {
      console.error('[Dashboard:UptimeBars] Fetch error:', e)
    }
  }

  // ==================== Rendering ====================

  renderBars() {
    // Generate date range using UTC dates (to match server-side DATE() which uses UTC)
    const dates = []
    const today = new Date()

    for (let i = this.daysValue - 1; i >= 0; i--) {
      const date = new Date(today)
      date.setUTCDate(date.getUTCDate() - i)
      dates.push(date)
    }

    // Clear and rebuild bars
    this.element.innerHTML = ''

    dates.forEach((date) => {
      // Format date as YYYY-MM-DD using UTC (matches server's DATE() format)
      const dateKey = this.formatDateKey(date)
      const dayData = this.heatmapData[dateKey]

      const bar = document.createElement('div')
      bar.className = 'flex-1 h-6 rounded cursor-pointer transition-all hover:scale-y-110'
      bar.style.backgroundColor = this.getBarColor(dayData)

      // Store data for tooltip
      const tooltipContent = this.getTooltipContent(date, dayData)

      bar.addEventListener('mouseenter', (e) => this.showTooltip(e, tooltipContent))
      bar.addEventListener('mouseleave', () => this.hideTooltip())

      this.element.appendChild(bar)
    })
  }

  formatDateKey(date) {
    // Format as YYYY-MM-DD using UTC timezone (matches server's UTC dates)
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  getBarColor(dayData) {
    // No data or no checks - show muted/gray
    if (!dayData || !dayData.total_checks || dayData.total_checks === 0) {
      return 'var(--bg-muted, #e5e7eb)'
    }

    const uptime = dayData.uptime_percentage

    if (uptime === null || uptime === undefined) {
      return 'var(--bg-muted, #e5e7eb)'
    }

    // Color based on uptime percentage
    if (uptime >= 99.9) return 'var(--color-success-500, #22c55e)'
    if (uptime >= 99) return 'var(--color-success-400, #4ade80)'
    if (uptime >= 95) return 'var(--color-warning-400, #facc15)'
    if (uptime >= 90) return 'var(--color-warning-500, #f59e0b)'
    return 'var(--color-error-500, #ef4444)'
  }

  getTooltipContent(date, dayData) {
    const dateStr = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    })

    if (!dayData || !dayData.total_checks || dayData.total_checks === 0) {
      return `
        <div style="font-weight: 600; margin-bottom: 2px;">${dateStr}</div>
        <div style="color: var(--text-muted, #9ca3af);">No data</div>
      `
    }

    const uptime = dayData.uptime_percentage
    const checks = dayData.total_checks
    const responseTime = dayData.avg_response_time

    // Determine status color
    let statusColor = '#22c55e' // green
    let _statusText = 'Operational'
    if (uptime < 90) {
      statusColor = '#ef4444' // red
      _statusText = 'Major Issues'
    } else if (uptime < 99) {
      statusColor = '#f59e0b' // yellow
      _statusText = 'Degraded'
    }

    return `
      <div style="font-weight: 600; margin-bottom: 4px;">${dateStr}</div>
      <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
        <span style="width: 8px; height: 8px; border-radius: 50%; background: ${statusColor};"></span>
        <span>${uptime?.toFixed(1) || 0}% uptime</span>
      </div>
      <div style="color: var(--text-muted, #9ca3af); font-size: 11px;">
        ${checks} check${checks !== 1 ? 's' : ''}${responseTime ? ` • ${Math.round(responseTime)}ms avg` : ''}
      </div>
    `
  }
}
